import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { baseParticipationAggregation, participationPreMatch } from '@adornis/buchungsheld-db/api/statistic.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import type { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { fillInEmpty } from '@adornis/buchungsheld-db/util.js';
import {
  icsrmGetCitizenshipFromCountry,
  icsrmGetDaadRegionFromCountry,
} from '../institution-statistics/icsrm-helper.js';
import { TimeframeParticipationCountByImigrationHistory } from '../institution-statistics/icsrm-institiiution-statistics-plugin.js';
import { IcsrmDesiredDegreeEnum, IcsrmUniEnum } from '../stammdaten/icsrm-stammdaten-plugin.js';

export const getIcsrmGenderDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getIcsrmGenderDistribution',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => [String], name: 'states' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, states: ParticipationState[]) => {
    return async () => {
      const aggregation = [
        ...(await participationPreMatch(filter, states)),
        ...baseParticipationAggregation,
        { $match: { state: { $in: states }, ...filter.getMongoFilter() } },
        // count by gender
        {
          $group: {
            _id: { gender: '$userStammdaten.gender' },
            total: { $sum: 1 },
            gender: { $first: '$userStammdaten.gender' },
          },
        },
      ];

      const participatonCollection = mongo().collection(Participation._collectionName);
      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result.filter(group => !!group.gender).map(group => [group.gender, group.total]);
    };
  },
});

export const getDaadCountryDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getDaadCountryDistribution',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => [String], name: 'states' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, states: ParticipationState[]) => {
    return async () => {
      const participatonCollection = mongo().collection(Participation._collectionName);
      const aggregation = [
        ...(await participationPreMatch(filter, states)),
        ...baseParticipationAggregation,
        { $match: { state: { $in: states }, ...filter.getMongoFilter() } },
        // count by country
        {
          $group: {
            _id: { country: '$userStammdaten.countryOfBirth' },
            total: { $sum: 1 },
            country: { $first: '$userStammdaten.countryOfBirth' },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      const mappedGroups = result
        .filter(group => !!group.country)
        .reduce((acc, group) => {
          const country = group.country;
          const count = group.total;
          const region = icsrmGetDaadRegionFromCountry(country);
          if (!region) return acc; // there are some countries that should be ignored
          acc[region] = acc[region] ?? [region, 0];
          acc[region][1] += count;
          return acc;
        }, {});

      return Object.values(mappedGroups);
    };
  },
});

export const getCitizenshipDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getCitizenshipDistribution',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => [String], name: 'states' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, states: ParticipationState[]) => {
    return async () => {
      const participatonCollection = mongo().collection(Participation._collectionName);
      const aggregation = [
        ...(await participationPreMatch(filter, states)),
        ...baseParticipationAggregation,
        { $match: { state: { $in: states }, ...filter.getMongoFilter() } },
        // count by country
        {
          $group: {
            _id: { country: '$userStammdaten.nationality' },
            total: { $sum: 1 },
            country: { $first: '$userStammdaten.nationality' },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      const mappedGroups = result
        .filter(group => !!group.country)
        .reduce((acc, group) => {
          const country = group.country;
          const count = group.total;
          const region = icsrmGetCitizenshipFromCountry(country);
          if (!region) return acc; // there are some countries that should be ignored
          acc[region] = acc[region] ?? [region, 0];
          acc[region][1] += count;
          return acc;
        }, {});

      logger.debug({ mappedGroups }, 'groups');

      return Object.values(mappedGroups);
    };
  },
});

export const getUniDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getUniDistribution',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => [String], name: 'states' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, states: ParticipationState[]) => {
    return async () => {
      const participatonCollection = mongo().collection(Participation._collectionName);
      const aggregation = [
        ...(await participationPreMatch(filter, states)),
        ...baseParticipationAggregation,
        { $match: { state: { $in: states }, ...filter.getMongoFilter() } },
        // count by Hochschule
        {
          $group: {
            _id: { uni: '$userStammdaten.currentUni' },
            total: { $sum: 1 },
            uni: { $first: '$userStammdaten.currentUni' },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result
        .filter(group => !!group.uni)
        .map(group => [group.uni, group.total])
        .sort((a, b) => {
          // sort by enum order
          const index_a = Object.keys(IcsrmUniEnum).indexOf(a[0]);
          const index_b = Object.keys(IcsrmUniEnum).indexOf(b[0]);
          return index_a - index_b;
        });
    };
  },
});

export const getDesiredDegreeDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getDesiredDegreeDistribution',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => [String], name: 'states' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, states: ParticipationState[]) => {
    return async () => {
      const participatonCollection = mongo().collection(Participation._collectionName);
      const aggregation = [
        ...(await participationPreMatch(filter, states)),
        ...baseParticipationAggregation,
        { $match: { state: { $in: states }, ...filter.getMongoFilter() } },
        // count by Hochschule
        {
          $group: {
            _id: { degree: '$userStammdaten.desiredDegree' },
            total: { $sum: 1 },
            degree: { $first: '$userStammdaten.desiredDegree' },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result
        .filter(group => !!group.degree)
        .map(group => [group.degree, group.total])
        .sort((a, b) => {
          // sort by enum order
          const index_a = Object.keys(IcsrmDesiredDegreeEnum).indexOf(a[0]);
          const index_b = Object.keys(IcsrmDesiredDegreeEnum).indexOf(b[0]);
          return index_a - index_b;
        });
    };
  },
});

export const getUnterbuchungenPerWeek = registerQuery({
  type: () => [[String, String, Int]],
  operationName: 'getUnterbuchungenPerWeek',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => Boolean, name: 'onlyForpartiicipantsWithImmigrationHistory' },
    { type: () => Int, name: 'limit' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, onlyForpartiicipantsWithImmigrationHistory: boolean, limit: number) => {
    return async () => {
      const productCollection = mongo().collection(Product._collectionName);
      const aggregation = [
        {
          $match: {
            _class: Workshop._class,
            ...filter.getMongoFilter(),
            appointments: { $elemMatch: { $exists: true } }, // at least one appointment
            'appointments.0.start': { $exists: true },
          },
        },
        // calculate number of participants
        {
          $lookup: {
            from: Participation._collectionName,
            localField: '_id',
            foreignField: 'productID',
            pipeline: [
              // lookup stammdaten
              {
                $lookup: {
                  from: UserStammdaten._collectionName,
                  localField: 'userID',
                  foreignField: 'userID',
                  as: 'userStammdaten',
                },
              },
              // filter stammdaen for product
              {
                $addFields: {
                  userStammdaten: {
                    $first: {
                      $filter: {
                        input: '$userStammdaten',
                        as: 'userStammdaten',
                        cond: { $eq: ['$$userStammdaten.productID', '$participations.productID'] },
                      },
                    },
                  },
                },
              },
            ],
            as: 'participations',
          },
        },

        // maybe filter out non immigration participations
        ...(onlyForpartiicipantsWithImmigrationHistory
          ? [
              {
                $addFields: {
                  participations: {
                    $filter: {
                      input: '$participations',
                      as: 'participation',
                      cond: {
                        $or: [
                          { $eq: ['$$participation.userStammdaten.isImmigrant', true] },
                          { $ne: ['$$participation.countryOfBirth', 'DE'] },
                        ],
                      },
                    },
                  },
                },
              },
            ]
          : []),

        // get stuff
        { $addFields: { start: { $first: '$appointments.start' }, participationCount: { $size: '$participations' } } },

        // calc ratio
        {
          $addFields: {
            ratio: { $min: [{ $multiply: [100, { $divide: ['$participationCount', '$capacity'] }] }, 100] },
          },
        },

        // group by week
        {
          $project: {
            capacity: 1,
            participationCount: 1,
            ratio: 1,
            week: { $week: '$start' },
            year: { $year: '$start' },
          },
        },
        {
          $group: {
            _id: { week: '$week', year: '$year' },
            total: { $avg: '$ratio' },
            week: { $first: '$week' },
            year: { $first: '$year' },
          },
        },
        { $match: { year: { $type: 'number' }, week: { $type: 'number' } } },
      ];

      const result = await productCollection
        .aggregate(aggregation)
        .sort({ year: -1, week: -1 }) // sort desc to get most current results
        .limit(limit)
        .toArray();

      const registrationsOverTime = fillInEmpty(result as any);
      return registrationsOverTime.slice(-1 * limit).map(({ week, year, total }) => [week, year, total]);
    };
  },
});

export const getParticipationCountGroupedByTimeframeAndImigrationHistory = registerQuery({
  type: () => [TimeframeParticipationCountByImigrationHistory],
  operationName: 'getParticipationCountGroupedByTimeframeAndImigrationHistory',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => [String], name: 'states' },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_DASHBOARD, params.filter.institutionID),
    ),
  ],
  resolve: (filter: BHFilter, states: ParticipationState[]) => {
    return async (gqlFields: BaseQLSelectionSet<TimeframeParticipationCountByImigrationHistory>) => {
      const aggregation = [
        ...(await participationPreMatch(filter, states)),
        ...baseParticipationAggregation,
        { $match: { state: { $in: states }, ...filter.getMongoFilter() } },
        { $addFields: { isImmigrant: { $eq: ['$userStammdaten.isImmigrant', true] } } },
        {
          $group: {
            _id: { timeframeID: '$product.timeframeID', isImmigrant: '$isImmigrant' },
            total: { $sum: 1 },
            timeframeID: { $first: '$product.timeframeID' },
            isImmigrant: { $first: '$isImmigrant' },
          },
        },
      ];

      const participatonCollection = mongo().collection(Participation._collectionName);
      const result = await participatonCollection.aggregate(aggregation).toArray();

      const counts = result.reduce((acc, r) => {
        const timeframeID = r.timeframeID;
        if (!timeframeID) return acc;
        const tpCount =
          acc[timeframeID] || new TimeframeParticipationCountByImigrationHistory({ timeframeID, counts: [] });
        acc[timeframeID] = tpCount;
        tpCount.counts.push([!!r.isImmigrant, r.total]);
        return acc;
      }, {});

      return Object.values(counts);
    };
  },
});
